/**
 * LeadForm — pre-chat lead capture form component.
 *
 * Renders configured form fields before the chat starts. When the user
 * submits, posts data to POST /v1/sites/{siteId}/leads and transitions to chat.
 */

import { useState } from 'preact/hooks';
import type { LeadFormField, LeadCaptureConfig } from '../lib/types';
import { leadStorageKey } from '../lib/storage';
import { getWidgetApiClient } from '../lib/api';
import { isValidEmail, isPlausiblePhone } from '../lib/validation';

interface LeadFormProps {
  config: LeadCaptureConfig;
  apiBase: string;
  token: string;
  siteId: string;
  accentColor: string;
  onSubmitted: (leadId: string, conversationId?: string) => void;
}

export function LeadForm({ config, apiBase, token, siteId, accentColor, onSubmitted }: LeadFormProps): preact.JSX.Element {
  const [values, setValues] = useState<Record<string, string | boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const updateField = (key: string, value: string | boolean): void => {
    setValues((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    for (const field of config.fields) {
      const val = values[field.key];

      if (field.required) {
        if (field.type === 'checkbox') {
          if (val !== true) {
            newErrors[field.key] = 'This field is required';
          }
        } else if (!val || (typeof val === 'string' && !val.trim())) {
          newErrors[field.key] = 'This field is required';
        }
      }

      if (field.type === 'email' && val && typeof val === 'string' && val.trim()) {
        if (!isValidEmail(val.trim())) {
          newErrors[field.key] = 'Invalid email address';
        }
      }

      if (field.type === 'tel' && val && typeof val === 'string' && val.trim()) {
        if (!isPlausiblePhone(val.trim())) {
          newErrors[field.key] = 'Please enter a valid phone number';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: Event): Promise<void> => {
    e.preventDefault();
    setSubmitError('');

    if (!validate()) return;

    setSubmitting(true);
    try {
      const apiClient = getWidgetApiClient(siteId);
      const { lead_id: leadId, conversation_id: convId } = await apiClient.submitLead(
        siteId,
        token,
        values,
        window.location.href,
      );

      // Store in sessionStorage so we don't re-show the form on refresh.
      try {
        sessionStorage.setItem(leadStorageKey(siteId), JSON.stringify({ leadId, conversationId: convId }));
      } catch (err) {
        console.error('[ch-widget] sessionStorage write failed', err);
      }

      onSubmitted(leadId, convId);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  // Preview mode: apiBase is '' — relay through postMessage bridge.
  // The getWidgetApiClient call above already returns the preview client in that case
  // because registerWidgetApiClient registers it at init time.
  // We keep apiBase in props for potential future use but it's not read here.
  void apiBase;

  const renderField = (field: LeadFormField): preact.JSX.Element => {
    const hasError = !!errors[field.key];
    const errorClass = hasError ? ' yd-lead-field-error' : '';

    switch (field.type) {
      case 'checkbox':
        return (
          <label class={`yd-lead-checkbox${errorClass}`}>
            <input
              type="checkbox"
              checked={values[field.key] === true}
              onChange={(e) => updateField(field.key, (e.target as HTMLInputElement).checked)}
            />
            <span>{field.label}{field.required ? ' *' : ''}</span>
          </label>
        );

      case 'select':
        return (
          <div class={`yd-lead-field${errorClass}`}>
            <label class="yd-lead-label">
              {field.label}{field.required ? ' *' : ''}
            </label>
            <select
              class="yd-lead-select"
              value={(values[field.key] as string) || ''}
              onChange={(e) => updateField(field.key, (e.target as HTMLSelectElement).value)}
            >
              <option value="">{field.placeholder || 'Select...'}</option>
              {(field.options || []).map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            {hasError && <span class="yd-lead-error-text">{errors[field.key]}</span>}
          </div>
        );

      default:
        return (
          <div class={`yd-lead-field${errorClass}`}>
            <label class="yd-lead-label">
              {field.label}{field.required ? ' *' : ''}
            </label>
            <input
              type={field.type}
              class="yd-lead-input"
              placeholder={field.placeholder || ''}
              value={(values[field.key] as string) || ''}
              onInput={(e) => updateField(field.key, (e.target as HTMLInputElement).value)}
            />
            {hasError && <span class="yd-lead-error-text">{errors[field.key]}</span>}
          </div>
        );
    }
  };

  return (
    <div class="yd-lead-form-container">
      {config.title && (
        <p class="yd-lead-title">{config.title}</p>
      )}
      <form class="yd-lead-form" onSubmit={handleSubmit}>
        {config.fields.map((field) => (
          <div key={field.key}>{renderField(field)}</div>
        ))}

        {submitError && (
          <div class="yd-lead-submit-error">{submitError}</div>
        )}

        <button
          type="submit"
          class="yd-lead-submit"
          disabled={submitting}
          style={{ backgroundColor: accentColor }}
        >
          {submitting ? 'Submitting...' : (config.submit_label || 'Start Chat')}
        </button>

        {config.privacy_text && (
          <p class="yd-lead-privacy">{config.privacy_text}</p>
        )}
      </form>
    </div>
  );
}
